import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import { useParams, Link, useBlocker } from "@tanstack/react-router";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { apiService } from "../services/api";
import { extractAndUploadBase64Images } from "../lib/articleImageUtils";
import { buildApiUrl } from "../lib/config";
import { ApiRequestError, getApiErrorMessage } from "../lib/apiErrors";
import { useForm } from "react-hook-form";
import {
  useArticle,
  useUpdateArticle,
  useDeleteArticle,
  useArchiveArticle,
  useRestoreArticle,
  usePublishArticle,
  useUnpublishArticle,
  useRecordArticleView,
  useArticleFeedbackSummary,
  useArticleFeedbackList,
  useArticleDraft,
  useSaveArticleDraft,
  usePublishArticleDraft,
  useDiscardArticleDraft,
  useCancelArticleSchedule,
  useMarkArticleReviewed,
} from "../hooks/useArticles";
import { ArticlePreviewDialog } from "../components/knowledge-base/ArticlePreviewDialog";
import { ArticleRevisionHistory } from "../components/knowledge-base/ArticleRevisionHistory";
import { ScheduleArticleDialog } from "../components/knowledge-base/ScheduleArticleDialog";
import { showToast } from "../lib/toast";
import { useArticleCollections } from "../hooks/useArticleCollections";
import { TreeSelect } from "../components/tree-select/TreeSelect";
import { collectionToOption } from "../components/tree-select/treeOptions";
import { useAdminOrganizations } from "../hooks/useAdminOrganizations";
import { useArticleStatuses } from "../hooks/useLookups";
import { useDateFormat } from "../hooks/useDateFormat";
import type { SaveArticleDraftRequest, UpdateArticleRequest } from "../types/articles";
import {
  ArticleStatus,
  ArticleStatusLabels,
  ArticleStatusColors,
  ArticleVisibility,
  ArticleVisibilityLabels,
  ArticleVisibilityColors,
  ArticleLinkTypeLabels,
  ArticleLinkTypeColors,
  type ArticleLinkType,
} from "../types/articles";
import { useArticleTicketLinks } from "../hooks/useTicketArticleLinks";
import { OrganizationBadge } from "../components/OrganizationBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { RichTextEditor } from "../components/RichTextEditor";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Trash2,
  Eye,
  Calendar,
  User,
  Tag,
  Star,
  Send,
  MoreVertical,
  FileText,
  Ticket,
  ThumbsUp,
  ThumbsDown,
  Archive,
  ArchiveRestore,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  FilePen,
  Info,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

interface UpdateArticleForm {
  title: string;
  summary: string;
  content: string;
  tags: string;
  visibility: number;
  collectionId: string;
  isFeatured: boolean;
}

// What an edit starts from: the version it opened, so a save can be refused when someone else changed it since
interface EditBase {
  updatedAt: string;
  // The pending draft's updatedAt when the edit opened the unpublished changes of a published article
  draftUpdatedAt: string | null;
  content: string;
}

type SaveAction = "save" | "publish" | "draft";

export function ArticleDetail() {
  const { id } = useParams({ from: "/helpdesk/knowledge-base/$id" });
  const { navigateTo, getPath } = useHelpdeskNavigation();
  const { formatDateTime } = useDateFormat();
  const { isCustomerPortal, availableOrganizations } = useAuth();

  const { data: article, isLoading, error, isError, refetch: refetchArticle } = useArticle(id);
  const isPublished = article?.status === ArticleStatus.Published;
  // A published article's unpublished changes - its text is edited there, while readers keep the live version
  const hasPendingDraft = isPublished && !!article?.hasDraft;
  // Follows the article: a new draftUpdatedAt fetches that version of the draft
  const { data: draft, isLoading: isDraftLoading } = useArticleDraft(
    id,
    hasPendingDraft ? article?.draftUpdatedAt : null
  );
  const { data: collections = [] } = useArticleCollections(article?.organizationId);
  const { data: articleStatuses = [] } = useArticleStatuses();
  const { data: ticketLinksData } = useArticleTicketLinks(id, article?.organizationId);
  // Wait for the article so the first request carries its organization - without one the API falls back to the
  // user's first organization and would cache the wrong (usually empty) feedback for articles in other orgs
  const feedbackArticleId = article ? id : undefined;
  const { data: feedbackSummary } = useArticleFeedbackSummary(feedbackArticleId, article?.organizationId);
  const [feedbackPage, setFeedbackPage] = useState(1);
  const FEEDBACK_PAGE_SIZE = 20;
  // Another article starts at its newest feedback
  useEffect(() => setFeedbackPage(1), [id]);
  const { data: feedbackListData } = useArticleFeedbackList(feedbackArticleId, feedbackPage, FEEDBACK_PAGE_SIZE, article?.organizationId);

  const updateArticleMutation = useUpdateArticle();
  const saveDraftMutation = useSaveArticleDraft();
  const publishDraftMutation = usePublishArticleDraft();
  const discardDraftMutation = useDiscardArticleDraft();
  const cancelScheduleMutation = useCancelArticleSchedule();
  const markReviewedMutation = useMarkArticleReviewed();
  // The last save's failure, whichever request it was - shown in the editor until the next save
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [savingAction, setSavingAction] = useState<SaveAction | null>(null);
  const isEditConflict = saveError instanceof ApiRequestError && saveError.status === 409;
  const deleteArticleMutation = useDeleteArticle();
  const archiveArticleMutation = useArchiveArticle();
  const restoreArticleMutation = useRestoreArticle();
  const publishArticleMutation = usePublishArticle();
  const unpublishArticleMutation = useUnpublishArticle();
  const recordViewMutation = useRecordArticleView();

  const [editMode, setEditMode] = useState(false);
  const [content, setContent] = useState("");
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  // The version the editor opened (updatedAt as the server sent it). Saving sends it back, and the server refuses the
  // save if someone else changed the article since, rather than silently overwriting their changes.
  const [editBase, setEditBase] = useState<EditBase | null>(null);
  const [preview, setPreview] = useState<{ title: string; summary?: string | null; content: string; label?: string } | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [discardDraftOpen, setDiscardDraftOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    setValue,
    trigger,
    watch,
  } = useForm<UpdateArticleForm>();

  // Unsaved-changes guard: active only while the user is in edit mode AND has actually changed something.
  // `isDirty` covers the title/summary/tags/category fields (tracked by react-hook-form against the last reset).
  // The rich-text content is held in a separate `content` state and compared against the article's saved content.
  const isContentDirty = editMode && editBase != null && content !== editBase.content;
  const isFormDirty = editMode && (isDirty || isContentDirty);
  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  const collectionOptions = (Array.isArray(collections) ? collections : []).map(collectionToOption);
  const safeArticleStatuses = Array.isArray(articleStatuses) ? articleStatuses : [];
  const [hasRecordedView, setHasRecordedView] = useState(false);

  // Helper functions to get status labels and colors from fetched data
  const getStatusLabel = (statusValue: number): string => {
    const status = safeArticleStatuses.find(s => s.value === statusValue);
    return status?.label || ArticleStatusLabels[statusValue as ArticleStatus] || 'Unknown';
  };

  const getStatusColor = (statusValue: number): string => {
    // Keep using hardcoded colors for now, but get label from API
    return ArticleStatusColors[statusValue as ArticleStatus] || 'bg-gray-100 text-gray-800';
  };

  // Record article view when component loads (only once)
  // Only record views for customers viewing published articles
  useEffect(() => {
    if (article && !editMode && !hasRecordedView && isCustomerPortal && article.status === ArticleStatus.Published) {
      recordViewMutation.mutate(article.id);
      setHasRecordedView(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id, isCustomerPortal]); // Only depend on article ID and portal type

  // Initialize form when article loads - but not while editing: a background refetch (another tab, a reconnect, a view
  // counted) would otherwise reset the form and throw away the edits being made
  useEffect(() => {
    if (article && !editMode) {
      const formData = {
        title: article.title,
        summary: article.summary || "",
        content: article.content,
        tags: article.tags || "",
        visibility: article.visibility ?? ArticleVisibility.Public,
        collectionId: article.collectionId || "",
        isFeatured: article.isFeatured,
      };
      reset(formData);
      setContent(article.content);
    }
  }, [article, editMode, reset]);

  const handleEditModeToggle = () => {
    if (!editMode && article) {
      // A published article with unpublished changes is edited from those changes; its settings are the article's
      const text = hasPendingDraft && draft ? draft : article;
      const formData = {
        title: text.title,
        summary: text.summary || "",
        content: text.content,
        tags: text.tags || "",
        visibility: article.visibility ?? ArticleVisibility.Public,
        collectionId: article.collectionId || "",
        isFeatured: article.isFeatured,
      };
      reset(formData);
      setContent(text.content);
      setEditBase({
        updatedAt: article.updatedAt,
        draftUpdatedAt: hasPendingDraft && draft ? draft.updatedAt : null,
        content: text.content,
      });
      // A failed save from an earlier edit isn't this edit's problem
      setSaveError(null);
    }
    setEditMode(!editMode);
  };

  // After a refused save (someone else changed the article): drop these edits and start again from their version
  const handleDiscardAndReload = async () => {
    setEditMode(false);
    setSaveError(null);
    await refetchArticle();
  };

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const orgId = article?.organizationId;
    const response = await apiService.uploadArticleImage(file, orgId);
    if (!response.success || !response.data) {
      throw new Error(getApiErrorMessage(response, 'Failed to upload image'));
    }
    return buildApiUrl(`articles/images/${response.data.id}`);
  }, [article?.organizationId]);

  /**
   * Saves the edit. A draft article is saved directly ("save"). A published article's changes either go live
   * ("publish") - directly when the edit didn't start from unpublished changes, otherwise by saving and publishing
   * those - or wait as unpublished changes ("draft") while readers keep the live version.
   */
  const saveEdit = async (data: UpdateArticleForm, action: SaveAction) => {
    if (!article || !editMode || !editBase) {
      return;
    }

    // Trigger content validation
    const isContentValid = await trigger("content");
    if (!isContentValid) {
      return;
    }

    setSavingAction(action);
    setSaveError(null);
    try {
      // Upload any remaining base64 images in content (e.g. from rich HTML paste)
      const processedContent = await extractAndUploadBase64Images(content, article?.organizationId);

      if (action === "draft" || (action === "publish" && editBase.draftUpdatedAt)) {
        const draftRequest: SaveArticleDraftRequest = {
          title: data.title,
          content: processedContent,
          summary: data.summary || undefined,
          tags: data.tags || undefined,
          visibility: data.visibility as ArticleVisibility,
          collectionId: data.collectionId || undefined,
          isFeatured: data.isFeatured,
          expectedDraftUpdatedAt: editBase.draftUpdatedAt ?? undefined,
          expectedUpdatedAt: editBase.updatedAt,
        };
        const saved = await saveDraftMutation.mutateAsync({ articleId: article.id, request: draftRequest });
        if (action === "publish") {
          await publishDraftMutation.mutateAsync({ articleId: article.id, request: { expectedDraftUpdatedAt: saved.updatedAt } });
          showToast.success("Changes Published", `Readers now see the updated "${data.title}".`);
        } else {
          showToast.success("Saved as Unpublished Changes", "Readers keep seeing the published version until you publish them.");
        }
      } else {
        // No status: publishing and unpublishing are their own actions, and sending back the status this page loaded
        // would undo one someone made since
        const request: UpdateArticleRequest = {
          title: data.title,
          content: processedContent,
          summary: data.summary || undefined,
          tags: data.tags || undefined,
          visibility: data.visibility as ArticleVisibility,
          collectionId: data.collectionId || undefined,
          isFeatured: data.isFeatured,
          expectedUpdatedAt: editBase.updatedAt,
        };
        await updateArticleMutation.mutateAsync({ id: article.id, updates: request });
      }
      setEditMode(false);
    } catch (err) {
      // Shown in the editor's banner until the next save
      setSaveError(err instanceof Error ? err : new Error("An error occurred while saving. Please try again."));
    } finally {
      setSavingAction(null);
    }
  };

  // Publishes the pending changes as they were last saved (from the banner, outside the editor)
  const handlePublishDraft = async () => {
    if (!article || !draft) return;
    try {
      await publishDraftMutation.mutateAsync({ articleId: article.id, request: { expectedDraftUpdatedAt: draft.updatedAt } });
      showToast.success("Changes Published", `Readers now see the updated "${draft.title}".`);
    } catch (err) {
      showToast.error("Publish Failed", err instanceof Error ? err.message : "The changes could not be published.");
      // Someone saved the changes again since: show their version
      if (err instanceof ApiRequestError && err.status === 409) await refetchArticle();
    }
  };

  const handleDiscardDraft = async () => {
    if (!article) return;
    try {
      await discardDraftMutation.mutateAsync(article.id);
    } catch {
      // Shown by the mutation hook
    }
  };

  const openPreviewOfEdit = () => {
    setPreview({
      title: watch("title") || article?.title || "",
      summary: watch("summary"),
      content,
      label: "your edit",
    });
  };

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setValue("content", newContent, { shouldValidate: false }); // Prevent validation on every keystroke
  };

  // Register content field with validation
  register("content", {
    required: "Content is required",
    minLength: {
      value: 10,
      message: "Content must be at least 10 characters long",
    },
    validate: () => {
      if (content.trim().length < 10) {
        return "Content must be at least 10 characters long";
      }
      return true;
    },
  });

  // Archived articles are read-only; permanent delete is for Admins of the article's organization, from the archive
  const isArchived = article?.status === ArticleStatus.Archived;
  const isSaving = savingAction !== null || isSubmitting;
  // Where customers read it - only a published public article is there
  const portalSubdomain = availableOrganizations?.find((org) => org.id === article?.organizationId)?.subdomain;
  const portalArticleUrl =
    article && portalSubdomain && isPublished && article.visibility === ArticleVisibility.Public
      ? `/org/${portalSubdomain}/portal/knowledge-base/${article.id}`
      : null;
  const { data: adminOrgs } = useAdminOrganizations();
  const isAdminForArticle =
    !!article && (adminOrgs ?? []).some((org) => org.id === article.organizationId);

  const handleArchive = async () => {
    if (!article) return;
    try {
      await archiveArticleMutation.mutateAsync(article.id);
    } catch {
      // Error is handled in the mutation hook
    }
  };

  const handleRestore = async () => {
    if (!article) return;
    try {
      await restoreArticleMutation.mutateAsync(article.id);
    } catch {
      // Error is handled in the mutation hook
    }
  };

  const handleDelete = async () => {
    if (!article) return;
    try {
      await deleteArticleMutation.mutateAsync(article.id);
      navigateTo.knowledgeBase();
    } catch {
      // Error is handled in the mutation hook
    }
  };

  const handlePublish = async () => {
    if (!article) return;
    try {
      // No publishedAt: the server stamps its own clock. Sending the browser's clock could fail validation with
      // "Published date cannot be in the future" whenever the user's clock ran ahead of the server's.
      await publishArticleMutation.mutateAsync({
        id: article.id,
        request: {},
      });
    } catch {
      // Error is handled in the mutation hook
    }
  };

  const handleUnpublish = async () => {
    if (!article) return;
    try {
      await unpublishArticleMutation.mutateAsync(article.id);
    } catch {
      // Error is handled in the mutation hook
    }
  };



  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 bg-muted rounded animate-pulse" />
            <div className="space-y-2">
              <div className="h-8 w-64 bg-muted rounded animate-pulse" />
              <div className="h-4 w-48 bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-24 bg-muted rounded animate-pulse" />
            <div className="h-10 w-10 bg-muted rounded animate-pulse" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Skeleton */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="space-y-4">
                <div className="h-6 w-full bg-muted rounded animate-pulse" />
                <div className="h-6 w-5/6 bg-muted rounded animate-pulse" />
                <div className="h-6 w-4/6 bg-muted rounded animate-pulse" />
                <div className="h-40 w-full bg-muted rounded animate-pulse mt-6" />
              </div>
            </Card>
          </div>

          {/* Sidebar Skeleton */}
          <div>
            <Card className="p-4">
              <div className="space-y-4">
                <div className="h-6 w-32 bg-muted rounded animate-pulse" />
                <div className="space-y-3">
                  <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                  <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                  <div className="h-4 w-28 bg-muted rounded animate-pulse" />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !article) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Card className="p-8 max-w-md w-full mx-4">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <FileText className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Article Not Found
              </h2>
              <p className="text-muted-foreground text-sm">
                {error?.message || "The article you're looking for doesn't exist or has been removed."}
              </p>
            </div>
            <div className="flex gap-2 justify-center">
              <Link to={getPath.knowledgeBase()}>
                <Button variant="outline">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Knowledge Base
                </Button>
              </Link>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
              >
                Try Again
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // const watchedStatus = watch("status");

  return (
    <div className="space-y-4 md:space-y-6 px-4 sm:px-6 md:px-0">
      {/* Unsaved changes blocker — prompts before navigating away from edit mode with dirty state.
          Tab-close / reload is handled separately by the useBlocker's enableBeforeUnload option (native dialog). */}
      <AlertDialog
        open={blocker.status === "blocked"}
        onOpenChange={(open) => { if (!open) blocker.reset?.(); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to this article. If you leave now, your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Stay on page</AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>Discard and leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel-while-dirty confirmation — prompts when user clicks Cancel with unsaved changes.
          useBlocker doesn't catch this because Cancel is an internal state toggle, not a route navigation,
          and re-entering edit mode would silently wipe the form via reset(). This is the "save failed → Cancel"
          data-loss trap from the audit. */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to this article. If you cancel now, your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancelConfirmOpen(false)}>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setCancelConfirmOpen(false);
                handleEditModeToggle();
              }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Button variant="outline" size="icon" className="flex-shrink-0" asChild>
            <Link to={getPath.knowledgeBase()} aria-label="Back to the knowledge base">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h1 className="text-lg sm:text-2xl font-bold text-foreground truncate">
                {article.title}
              </h1>
              {article.isFeatured && (
                <Star className="h-5 w-5 text-yellow-500 fill-current flex-shrink-0" />
              )}
              <OrganizationBadge organizationName={article.organizationName} />
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              By {article.authorName} • Created {formatDateTime(article.createdAt)}
              {article.updatedAt !== article.createdAt && (
                <span> • Updated {formatDateTime(article.updatedAt)}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          {editMode ? (
            <>
              {isPublished ? (
                <>
                  <Button
                    type="button"
                    onClick={handleSubmit((data) => saveEdit(data, "publish"))}
                    disabled={isSaving}
                    className="w-full sm:w-auto"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {savingAction === "publish" ? "Publishing..." : "Publish Changes"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSubmit((data) => saveEdit(data, "draft"))}
                    disabled={isSaving}
                    className="w-full sm:w-auto"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingAction === "draft" ? "Saving..." : "Save as Draft"}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit((data) => saveEdit(data, "save"))}
                  disabled={isSaving}
                  className="w-full sm:w-auto"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingAction === "save" ? "Saving..." : "Save Changes"}
                </Button>
              )}
              <Button type="button" variant="outline" onClick={openPreviewOfEdit} className="w-full sm:w-auto">
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  // If the user has unsaved changes, confirm before discarding. Clean form → toggle immediately.
                  if (isFormDirty) {
                    setCancelConfirmOpen(true);
                  } else {
                    handleEditModeToggle();
                  }
                }}
                className="w-full sm:w-auto"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </>
          ) : (
            <>
              {!isArchived && (
                <Button
                  type="button"
                  onClick={handleEditModeToggle}
                  // Unpublished changes are edited from where they were left - wait for them
                  disabled={hasPendingDraft && (isDraftLoading || !draft)}
                  className="w-full sm:w-auto"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {hasPendingDraft ? "Edit Changes" : "Edit Article"}
                </Button>
              )}

              {/* Actions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="w-full sm:w-auto" aria-label="More article actions">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isArchived ? (
                    <DropdownMenuItem
                      onClick={handleRestore}
                      disabled={restoreArticleMutation.isPending}
                      className="cursor-pointer"
                    >
                      <ArchiveRestore className="h-4 w-4 mr-2" />
                      Restore to Draft
                    </DropdownMenuItem>
                  ) : article.status === ArticleStatus.Published ? (
                    <>
                      <DropdownMenuItem
                        onClick={() => markReviewedMutation.mutate(article.id)}
                        disabled={markReviewedMutation.isPending}
                        className="cursor-pointer"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Mark as Reviewed
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleUnpublish} className="cursor-pointer">
                        <X className="h-4 w-4 mr-2" />
                        Unpublish
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={handlePublish} className="cursor-pointer">
                        <Send className="h-4 w-4 mr-2" />
                        {article.scheduledPublishAt ? "Publish Now" : "Publish"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setScheduleOpen(true)} className="cursor-pointer">
                        <CalendarClock className="h-4 w-4 mr-2" />
                        {article.scheduledPublishAt ? "Change Schedule..." : "Schedule Publishing..."}
                      </DropdownMenuItem>
                    </>
                  )}

                  {!isArchived && (
                    <DropdownMenuItem
                      onClick={() => setPreview({ title: article.title, summary: article.summary, content: article.content })}
                      className="cursor-pointer"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Preview as Customer
                    </DropdownMenuItem>
                  )}
                  {portalArticleUrl && (
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <a href={portalArticleUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View in Portal
                      </a>
                    </DropdownMenuItem>
                  )}

                  {!isArchived && (
                    <>
                      <DropdownMenuSeparator />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="cursor-pointer">
                            <Archive className="h-4 w-4 mr-2" />
                            Archive Article
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Archive this article?</AlertDialogTitle>
                            <AlertDialogDescription>
                              "{article.title}" will be taken off the knowledge base and hidden from article lists,
                              search and suggestions. Tickets it's linked to keep the link. You can restore it to a
                              draft at any time.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleArchive}
                              disabled={archiveArticleMutation.isPending}
                            >
                              {archiveArticleMutation.isPending ? "Archiving..." : "Archive Article"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}

                  {isArchived && isAdminForArticle && (
                    <>
                      <DropdownMenuSeparator />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600 cursor-pointer">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Permanently
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Permanently delete this article?</AlertDialogTitle>
                            <AlertDialogDescription>
                              "{article.title}" will be deleted with its feedback and view history, and removed from
                              every ticket it's linked to. This can't be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDelete}
                              className="bg-red-600 hover:bg-red-700"
                              disabled={deleteArticleMutation.isPending}
                            >
                              {deleteArticleMutation.isPending ? "Deleting..." : "Delete Permanently"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Archived banner */}
      {isArchived && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
          <Archive className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="text-sm min-w-0">
            <p className="font-medium text-foreground">
              This article is archived
              {article.archivedAt ? ` (since ${formatDateTime(article.archivedAt)})` : ""}.
            </p>
            <p className="text-muted-foreground">
              It's off the knowledge base and read-only. Restore it to a draft to edit or publish it again.
            </p>
          </div>
        </div>
      )}

      {/* Unpublished changes of a published article */}
      {hasPendingDraft && !editMode && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
          <FilePen className="h-4 w-4 text-amber-700 dark:text-amber-400 flex-shrink-0 hidden sm:block" />
          <div className="text-sm min-w-0 flex-1">
            <p className="font-medium text-foreground">This article has unpublished changes</p>
            <p className="text-muted-foreground">
              {article.draftUpdatedAt ? `Saved ${formatDateTime(article.draftUpdatedAt)}` : "Saved"}
              {draft?.updatedByName ? ` by ${draft.updatedByName}` : ""}. Readers see the published version until
              they're published.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!draft}
              onClick={() => draft && setPreview({ title: draft.title, summary: draft.summary, content: draft.content, label: "unpublished changes" })}
            >
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </Button>
            <Button size="sm" onClick={handlePublishDraft} disabled={!draft || publishDraftMutation.isPending}>
              <Send className="h-4 w-4 mr-1" />
              {publishDraftMutation.isPending ? "Publishing..." : "Publish"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDiscardDraftOpen(true)} disabled={discardDraftMutation.isPending}>
              Discard
            </Button>
          </div>
        </div>
      )}

      {/* Scheduled publishing of a draft */}
      {article.status === ArticleStatus.Draft && article.scheduledPublishAt && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-blue-300 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/40">
          <CalendarClock className="h-4 w-4 text-blue-700 dark:text-blue-400 flex-shrink-0 hidden sm:block" />
          <p className="text-sm text-foreground flex-1">
            Scheduled to publish <span className="font-medium">{formatDateTime(article.scheduledPublishAt)}</span>.
            Edits made before then are published with it.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setScheduleOpen(true)}>
              Change
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => cancelScheduleMutation.mutate(article.id)}
              disabled={cancelScheduleMutation.isPending}
            >
              Cancel Schedule
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={discardDraftOpen} onOpenChange={setDiscardDraftOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard the unpublished changes?</AlertDialogTitle>
            <AlertDialogDescription>
              The changes waiting to be published are thrown away; the published article stays as it is. Earlier
              versions remain in the revision history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep them</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardDraft}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {preview && (
        <ArticlePreviewDialog
          open
          onOpenChange={(open) => !open && setPreview(null)}
          title={preview.title}
          summary={preview.summary}
          content={preview.content}
          label={preview.label}
        />
      )}

      {scheduleOpen && (
        <ScheduleArticleDialog
          open
          onOpenChange={setScheduleOpen}
          articleId={article.id}
          scheduledPublishAt={article.scheduledPublishAt}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Article Content */}
          <Card className="p-4 sm:p-6">
            {editMode ? (
              <div className="space-y-6">
                {isPublished && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      {editBase?.draftUpdatedAt
                        ? "You're editing this article's unpublished changes. "
                        : "This article is published. "}
                      <strong>Publish Changes</strong> puts your edit live; <strong>Save as Draft</strong> keeps it
                      unpublished while readers see the current version. Collection, visibility and featured apply as
                      soon as you save either way.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Inline save-error banner — sticks around until the next save succeeds,
                    so users can't miss a failed save like they can with an auto-dismissing toast. */}
                {saveError && (
                  // The destructive colour is a dark red in dark mode (made for backgrounds), unreadable as text
                  <Alert variant="destructive" className="dark:border-red-800 dark:text-red-300 dark:[&>svg]:text-red-300">
                    <AlertTitle>
                      {isEditConflict ? "Someone else changed this article" : "Save failed"}
                    </AlertTitle>
                    <AlertDescription>
                      <p>{saveError.message || "An error occurred while saving. Please try again."}</p>
                      {isEditConflict && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={handleDiscardAndReload}>
                            Discard my changes and reload
                          </Button>
                          <span className="text-xs">Copy anything you want to keep first.</span>
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    {...register("title", {
                      required: "Title is required",
                      validate: (value) =>
                        value.trim().length >= 3 || "Title must be at least 3 characters",
                      maxLength: {
                        value: 255,
                        message: "Title must be 255 characters or less",
                      },
                    })}
                    className={errors.title ? "border-red-500" : ""}
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Summary
                  </label>
                  <textarea
                    {...register("summary", {
                      maxLength: {
                        value: 500,
                        message: "Summary must be less than 500 characters",
                      },
                    })}
                    rows={3}
                    className={`w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                      errors.summary ? "border-red-500" : ""
                    }`}
                  />
                  {errors.summary && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.summary.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <RichTextEditor
                    content={content}
                    onChange={handleContentChange}
                    onImageUpload={handleImageUpload}
                    placeholder="Edit your article content..."
                    className={errors.content ? "border-red-500" : ""}
                  />
                  {errors.content && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.content.message}
                    </p>
                  )}
                </div>


                {/* Additional form fields moved to sidebar in edit mode */}
              </div>
            ) : (
              <div className="space-y-6">
                {article.summary && (
                  <div className="bg-muted/30 p-4 rounded-lg border">
                    <h3 className="font-semibold text-foreground mb-2">Summary</h3>
                    <p className="text-muted-foreground">{article.summary}</p>
                  </div>
                )}
                
                <div className="prose prose-sm sm:prose-base lg:prose-lg xl:prose-xl max-w-none dark:prose-invert">
                  <RichTextEditor
                    content={article.content}
                    onChange={() => {}}
                    editable={false}
                    className="border-none p-0"
                  />
                </div>
              </div>
            )}
          </Card>

        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Article Info */}
          <Card className="p-3 sm:p-4">
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
              Article Information
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <div className="flex flex-wrap items-center gap-1">
                  <Badge className={getStatusColor(article.status)}>
                    {getStatusLabel(article.status)}
                  </Badge>
                  {hasPendingDraft && <Badge variant="outline">Unpublished changes</Badge>}
                  {article.status === ArticleStatus.Draft && article.scheduledPublishAt && (
                    <Badge variant="outline">Scheduled</Badge>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Visibility</p>
                {editMode ? (
                  <select
                    {...register("visibility", { valueAsNumber: true })}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  >
                    <option value={ArticleVisibility.Public}>Public</option>
                    <option value={ArticleVisibility.Internal}>Internal</option>
                  </select>
                ) : (
                  <Badge className={ArticleVisibilityColors[article.visibility as ArticleVisibility]}>
                    {ArticleVisibilityLabels[article.visibility as ArticleVisibility] || 'Public'}
                  </Badge>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Collection</p>
                {editMode ? (
                  <TreeSelect
                    options={collectionOptions}
                    value={watch("collectionId") || null}
                    onChange={(id) => setValue("collectionId", id ?? "", { shouldDirty: true })}
                    placeholder="No collection"
                    noneLabel="No collection"
                    searchPlaceholder="Search collections..."
                    emptyMessage="No collections yet"
                  />
                ) : (
                  <p className="text-sm text-foreground">{article.collectionPath || article.collectionName || "No collection"}</p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Views</p>
                <div className="flex items-center gap-1">
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {article.viewCount.toLocaleString()}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Tags</p>
                {editMode ? (
                  <Input
                    {...register("tags")}
                    placeholder="Enter tags separated by commas"
                    className="text-sm"
                  />
                ) : article.tags ? (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {article.tags.split(",").map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag.trim()}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No tags</p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Featured</p>
                {editMode ? (
                  <div className="flex items-center space-x-2 mt-1">
                    <input
                      type="checkbox"
                      {...register("isFeatured")}
                      id="isFeatured"
                      className="rounded border-input text-primary focus:ring-ring"
                    />
                    <label
                      htmlFor="isFeatured"
                      className="text-sm font-medium text-foreground flex items-center gap-2"
                    >
                      <Star className="h-4 w-4" />
                      Featured Article
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    {article.isFeatured ? (
                      <>
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        <span className="text-sm text-foreground">Yes</span>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">No</span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Created</p>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {formatDateTime(article.createdAt)}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Updated</p>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {formatDateTime(article.updatedAt)}
                  </span>
                </div>
              </div>

              {isPublished && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Last Reviewed</p>
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">
                      {article.lastReviewedAt ? formatDateTime(article.lastReviewedAt) : "Never"}
                    </span>
                  </div>
                </div>
              )}

              {article.publishedAt && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Published</p>
                  <div className="flex items-center gap-1">
                    <Send className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">
                      {formatDateTime(article.publishedAt)}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-muted-foreground">Author</p>
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {article.authorName}
                  </span>
                </div>
              </div>

              {article.slug && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Slug</p>
                  <p className="text-sm text-foreground font-mono break-all">{article.slug}</p>
                </div>
              )}

            </div>
          </Card>

          <ArticleRevisionHistory article={article} disabled={editMode} />

          {/* Linked Tickets */}
          {ticketLinksData && ticketLinksData.count > 0 && (
            <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-3">
                <Ticket className="h-4 w-4" />
                Linked Tickets
                <span className="text-xs font-normal">({ticketLinksData.count})</span>
              </h3>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {ticketLinksData.ticketLinks.map((link) => (
                  <button
                    key={link.ticketId}
                    onClick={() => navigateTo.ticket(link.ticketId)}
                    className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors text-left cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground hover:text-primary truncate">
                        #{link.ticketNumber} — {link.subject}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge className={`text-[10px] px-1 py-0 ${ArticleLinkTypeColors[link.linkType as ArticleLinkType]}`}>
                          {ArticleLinkTypeLabels[link.linkType as ArticleLinkType]}
                        </Badge>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* Feedback Summary + Individual Entries */}
          {feedbackSummary && feedbackSummary.totalFeedback > 0 && (
            <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-3">
                Feedback
                <span className="text-xs font-normal">({feedbackSummary.totalFeedback})</span>
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Helpful</span>
                  <span className="font-medium text-green-600">{feedbackSummary.helpfulPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${feedbackSummary.helpfulPercentage}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{feedbackSummary.helpfulCount} helpful</span>
                  <span>{feedbackSummary.notHelpfulCount} not helpful</span>
                </div>
              </div>

              {/* Individual feedback entries, newest first, a page at a time. Makes customer comments visible to
                  staff — previously only the aggregated percentage was shown, so comments went unread. */}
              {feedbackListData && feedbackListData.feedback.length > 0 && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Recent responses</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {feedbackListData.feedback.map((entry) => (
                        <div
                          key={entry.id}
                          className="flex items-start gap-2 text-xs p-2 rounded-md bg-muted/30"
                        >
                          {entry.isHelpful ? (
                            <ThumbsUp className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <ThumbsDown className="h-3.5 w-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-muted-foreground">{formatDateTime(entry.createdAt)}</p>
                            {entry.comment && (
                              <p className="text-foreground mt-1 whitespace-pre-wrap break-words">
                                {entry.comment}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {feedbackListData.totalCount > FEEDBACK_PAGE_SIZE && (
                      <div className="flex items-center justify-between gap-2 mt-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={feedbackPage === 1}
                          onClick={() => setFeedbackPage((p) => Math.max(1, p - 1))}
                        >
                          Newer
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {(feedbackPage - 1) * FEEDBACK_PAGE_SIZE + 1}–
                          {(feedbackPage - 1) * FEEDBACK_PAGE_SIZE + feedbackListData.feedback.length} of {feedbackListData.totalCount}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={feedbackPage * FEEDBACK_PAGE_SIZE >= feedbackListData.totalCount}
                          onClick={() => setFeedbackPage((p) => p + 1)}
                        >
                          Older
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}